// Human-readable reasons the group-level bulk action buttons are disabled.
// The buttons themselves only encode the *conditions* in their `disabled`
// expressions; these helpers turn those conditions into a message shown on
// hover so operators aren't left guessing why a button is inactive.
//
// Each helper returns null when the button is enabled, and its conditions
// cover exactly the button's `disabled` expression (reason is non-null iff the
// button is disabled). The order of the checks is the display priority when
// several conditions are true at once.

import { NOT_OWNER_REASON } from '../../hooks/usePrActions';

/** Shown while another bulk operation is already running. */
export const BUSY_REASON = 'Another operation is in progress';

/** Shown while a wave-based deploy run is mid-flight. */
export const DEPLOY_RUN_ACTIVE_REASON = 'A deploy run is in progress';

/**
 * Reason the "Approve All" button is disabled. Mirrors the button's
 * `disabled` expression: selectedTargets.length === 0 || bulkBusy ||
 * !ownsGroup.
 */
export function approveAllDisabledReason(args: {
  ownsGroup: boolean;
  bulkBusy: boolean;
  selectedCount: number;
}): string | null {
  const { ownsGroup, bulkBusy, selectedCount } = args;
  if (!ownsGroup) return NOT_OWNER_REASON;
  if (bulkBusy) return BUSY_REASON;
  if (selectedCount === 0) return 'No PRs to approve';
  return null;
}

/**
 * Reason the "Merge Checked" button is disabled. Mirrors the button's
 * `disabled` expression: checkedCount === 0 || !checkedCanMerge || bulkBusy ||
 * !ownsGroup || (isDeployStep && deployRunActive).
 */
export function mergeCheckedDisabledReason(args: {
  ownsGroup: boolean;
  bulkBusy: boolean;
  checkedCount: number;
  checkedCanMerge: boolean;
  isDeployStep: boolean;
  deployRunActive: boolean;
}): string | null {
  const {
    ownsGroup,
    bulkBusy,
    checkedCount,
    checkedCanMerge,
    isDeployStep,
    deployRunActive,
  } = args;
  if (!ownsGroup) return NOT_OWNER_REASON;
  if (bulkBusy) return BUSY_REASON;
  if (checkedCount === 0) return 'Check at least one PR to merge';
  if (isDeployStep && deployRunActive) return DEPLOY_RUN_ACTIVE_REASON;
  if (!checkedCanMerge) return 'Some checked PRs cannot be merged yet';
  return null;
}

/**
 * Reason the "Deploy/Apply in waves" button is disabled. Mirrors the button's
 * `disabled` expression: bulkBusy || !ownsGroup || deploySectionCount === 0 ||
 * deployBlockedCount > 0. The deployRunActive check is a safety net: while a
 * run is in flight the button is replaced by Cancel, so it isn't rendered.
 */
export function deployWavesDisabledReason(args: {
  ownsGroup: boolean;
  bulkBusy: boolean;
  deployRunActive: boolean;
  deploySectionCount: number;
  deployBlockedCount: number;
}): string | null {
  const {
    ownsGroup,
    bulkBusy,
    deployRunActive,
    deploySectionCount,
    deployBlockedCount,
  } = args;
  if (!ownsGroup) return NOT_OWNER_REASON;
  if (bulkBusy) return BUSY_REASON;
  if (deployRunActive) return DEPLOY_RUN_ACTIVE_REASON;
  if (deploySectionCount === 0) return 'No PRs to deploy';
  if (deployBlockedCount > 0) {
    return deployBlockedCount === 1
      ? '1 PR cannot be merged yet'
      : `${deployBlockedCount} PRs cannot be merged yet`;
  }
  return null;
}
